/**
 * A minimal floating tool window: movable and resizable by pointer AND keyboard,
 * focus-trapped and Escape-aware, restoring focus to its opener on close. This
 * is the one place a react-rnd-style component is appropriate (ADR-UI-001);
 * it deliberately stays small and reuses the kit's focus-trap primitive.
 */
import { useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { IconButton } from "../ui/icon-button";
import { classNames } from "../ui/internal/class-names";
import { tabbableElements } from "../ui/internal/focus";
import { clampPosition, clampSize, KEYBOARD_MOVE_STEP, KEYBOARD_RESIZE_STEP } from "./geometry";
import type { PanelConstraints, PanelPosition, PanelSize } from "./types";

export interface FloatingPanelProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  /** Accessible name when `title` is not a plain string. */
  readonly label?: string;
  readonly closeLabel: string;
  /** Accessible name for the keyboard move/resize handle. */
  readonly moveLabel: string;
  readonly initialPosition?: PanelPosition;
  readonly initialSize?: PanelSize;
  readonly constraints?: PanelConstraints;
  readonly className?: string;
  readonly children: ReactNode;
}

const ARROW_DELTAS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

const DEFAULT_CONSTRAINTS: PanelConstraints = { minWidth: 200, minHeight: 140 };

export function FloatingPanel({
  children,
  className,
  closeLabel,
  constraints = DEFAULT_CONSTRAINTS,
  initialPosition = { x: 80, y: 80 },
  initialSize = { width: 320, height: 220 },
  label,
  moveLabel,
  onClose,
  open,
  title,
}: FloatingPanelProps): ReactNode {
  const windowRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState<PanelPosition>(initialPosition);
  const [size, setSize] = useState<PanelSize>(initialSize);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement ? active : null;
    const node = windowRef.current;
    const focusables = node === null ? [] : tabbableElements(node);
    (focusables[0] ?? node)?.focus();
    return () => {
      openerRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const ariaLabel = label ?? (typeof title === "string" ? title : moveLabel);

  const style: CSSProperties = {
    left: `${String(position.x)}px`,
    top: `${String(position.y)}px`,
    width: `${String(size.width)}px`,
    height: `${String(size.height)}px`,
  };

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const node = windowRef.current;
    if (node === null) return;
    const focusables = tabbableElements(node);
    if (focusables.length === 0) {
      event.preventDefault();
      node.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !node.contains(active))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  function handleHandleKeys(event: KeyboardEvent<HTMLButtonElement>): void {
    const delta = ARROW_DELTAS[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    if (event.shiftKey) {
      setSize((current) =>
        clampSize(
          { width: current.width + delta[0] * KEYBOARD_RESIZE_STEP, height: current.height + delta[1] * KEYBOARD_RESIZE_STEP },
          constraints,
        ),
      );
    } else {
      setPosition((current) =>
        clampPosition(
          { x: current.x + delta[0] * KEYBOARD_MOVE_STEP, y: current.y + delta[1] * KEYBOARD_MOVE_STEP },
          size,
        ),
      );
    }
  }

  function startDrag(event: ReactPointerEvent<HTMLElement>): void {
    if (event.button !== 0) return;
    if (event.target instanceof HTMLElement && event.target.closest("button") !== null) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = position;
    const move = (native: PointerEvent): void => {
      setPosition(clampPosition({ x: origin.x + (native.clientX - startX), y: origin.y + (native.clientY - startY) }, size));
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function startResize(event: ReactPointerEvent<HTMLElement>): void {
    if (event.button !== 0) return;
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = size;
    const move = (native: PointerEvent): void => {
      setSize(clampSize({ width: origin.width + (native.clientX - startX), height: origin.height + (native.clientY - startY) }, constraints));
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  return (
    <div
      aria-labelledby={titleId}
      className={classNames("thw-floating", className)}
      onKeyDown={handleKeyDown}
      ref={windowRef}
      role="dialog"
      style={style}
      tabIndex={-1}
    >
      <header className="thw-floating__header" onPointerDown={startDrag}>
        <IconButton
          className="thw-floating__handle"
          icon="✥"
          label={moveLabel}
          onKeyDown={handleHandleKeys}
          variant="ghost"
        />
        <h2 className="thw-floating__title" id={titleId}>
          {typeof title === "string" ? title : ariaLabel}
        </h2>
        <IconButton
          className="thw-icon-button--dismiss"
          icon="✕"
          label={closeLabel}
          onClick={onClose}
          variant="ghost"
        />
      </header>
      <div className="thw-floating__body">{children}</div>
      <span aria-hidden="true" className="thw-floating__resize" onPointerDown={startResize} />
    </div>
  );
}
